import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  Briefcase,
  FileStack,
  Building2,
  MessageSquare,
  BarChart3,
  CreditCard,
  Settings,
  Blocks,
  Newspaper,
} from "lucide-react";

export type PartnerNavItem = {
  label: string;
  href: string;
  icon: LucideIcon;
  badgeKey?: "applications" | "messages";
};

export type PartnerNavSection = {
  title: string;
  items: PartnerNavItem[];
};

/** Grouped IA — same calm structure as Skutopia Admin */
export const partnerNavSections: PartnerNavSection[] = [
  {
    title: "Work",
    items: [
      { label: "Home", href: "/partners/dashboard", icon: LayoutDashboard },
      {
        label: "Applications",
        href: "/partners/applications",
        icon: FileStack,
        badgeKey: "applications",
      },
      {
        label: "Messages",
        href: "/partners/messages",
        icon: MessageSquare,
        badgeKey: "messages",
      },
      {
        label: "Posts",
        href: "/partners/posts",
        icon: Newspaper,
      },
    ],
  },
  {
    title: "Programs",
    items: [
      { label: "Programs", href: "/partners/programs", icon: Briefcase },
      { label: "Reach", href: "/partners/analytics", icon: BarChart3 },
    ],
  },
  {
    title: "Organization",
    items: [
      { label: "Organization", href: "/partners/organization", icon: Building2 },
      { label: "Billing", href: "/partners/billing", icon: CreditCard },
      { label: "Integrations", href: "/partners/integrations", icon: Blocks },
      { label: "Settings", href: "/partners/settings", icon: Settings },
    ],
  },
];

/** Flat list kept for any legacy consumers */
export const partnerNavItems: PartnerNavItem[] = partnerNavSections.flatMap(
  (s) => s.items,
);

/** Categories partners publish → Flutter Discover filters */
export const opportunityTypes = [
  "Scholarships",
  "University Programs",
  "Summer Schools",
  "Short Courses",
  "Exchange Programs",
  "Internships",
  "Research Opportunities",
  "Competitions",
  "Fellowships",
  "Grants",
  "Post High School Programs",
  "Capacity Building",
  "Mentorship Programs",
  "Volunteer Programs",
  "Events",
  "Conferences",
];

/** Unified Discover audience — High school + Post High School are one feed. */
export const audienceTypes = [
  { value: "all", label: "All students" },
];

export const fundingTypes = [
  { value: "fullyFunded", label: "Fully funded" },
  { value: "partial", label: "Partial funding" },
  { value: "none", label: "Self-funded / fee-based" },
];

export const loginTrustIndicators = [
  "Reach high school students on Skutopia",
  "Publish scholarships, summer programs & placements",
  "Review applications in one place",
];

export const loginHighlights = [
  "List programs that appear in the Skutopia student app",
  "Reach high school learners preparing for university and careers",
  "Manage applications from one partner workspace",
  "Show your institution clearly with name, logo, and program details",
];

export const dashboardMetrics = [
  { key: "activeOpportunities", label: "Live on Discover", trend: "" },
  { key: "applications", label: "Applications", trend: "" },
  { key: "profileViews", label: "Student views", trend: "" },
  { key: "bookmarks", label: "Saved by students", trend: "" },
];

export const engagementChartData = [
  { month: "Jan", views: 0, applications: 0, bookmarks: 0 },
  { month: "Feb", views: 0, applications: 0, bookmarks: 0 },
  { month: "Mar", views: 0, applications: 0, bookmarks: 0 },
  { month: "Apr", views: 0, applications: 0, bookmarks: 0 },
  { month: "May", views: 0, applications: 0, bookmarks: 0 },
  { month: "Jun", views: 0, applications: 0, bookmarks: 0 },
];

export const geographicData = [
  { region: "East Africa", value: 0 },
  { region: "West Africa", value: 0 },
  { region: "Southern Africa", value: 0 },
  { region: "Other", value: 0 },
];

export const attentionItems: {
  title: string;
  type: string;
  urgency: "high" | "medium" | "low";
}[] = [];

export const recentActivity: {
  action: string;
  detail: string;
  time: string;
}[] = [];

export const mockApplications: unknown[] = [];
export const mockMessages: unknown[] = [];

export const analyticsInsights = [
  "Listings with clear grade ranges convert better with high school students.",
  "Fully funded programs earn more saves in the student app.",
  "Deadlines shown in Discover drive earlier applications.",
];
